import { isNative } from './native.ts'
import { followNativeApp } from './visibility.ts'

/** Storage policy per platform (ARCHITECTURE 9.11, ADR-011, docs/R0.md). Offline writes (the outbox) are only allowed where
 *  the browser will not silently erase them:
 *  - iOS/iPadOS (every browser: all use WebKit) and macOS Safari delete script-writable storage of NON-installed sites after
 *    ~7 days without interaction: the installed (standalone) app is REQUIRED there.
 *  - Chromium (Android, Windows, Linux, macOS Chrome/Edge) and Firefox: storage.persist() must be granted; installing is
 *    recommended, not enforced.
 *  - The native app (Capacitor, M19b): installed by definition, its storage is the app's own (no browser evicts it, no persistence to ask for):
 *    always field mode, never "install required", whatever its web view's user agent says. */
export type Os = 'ios' | 'android' | 'mac' | 'windows' | 'linux' | 'other'
export type Browser = 'safari' | 'chromium' | 'firefox' | 'other'
export type Mode = 'field' | 'online_only'
export interface PlatformInfo {
  os: Os; browser: Browser; standalone: boolean; persisted: boolean | null
  mode: Mode; reason?: 'install_required' | 'unsupported'; warnings: Array<'storage_not_persistent'>
}
export interface Env {
  ua: string; platform?: string; maxTouchPoints?: number
  displayModeStandalone: boolean; navigatorStandalone?: boolean
  hasIndexedDb: boolean; devOverride?: boolean
  /** running inside the native shell (`Capacitor.isNativePlatform()`) */
  native?: boolean
}

export function detect(env: Env, persisted: boolean | null = null): PlatformInfo {
  const ua = env.ua
  const iPadOs = /Macintosh/.test(ua) && (env.maxTouchPoints ?? 0) > 1       // iPadOS 13+ reports a Mac user agent
  const os: Os = /iPhone|iPad|iPod/.test(ua) || iPadOs ? 'ios'
    : /Android/.test(ua) ? 'android' : /Mac OS X|Macintosh/.test(ua) ? 'mac' : /Windows/.test(ua) ? 'windows' : /Linux|X11/.test(ua) ? 'linux' : 'other'
  const webkit = os === 'ios' || (os === 'mac' && /Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\/|OPR\/|Firefox\//.test(ua))
  const browser: Browser = webkit ? 'safari' : /Firefox\//.test(ua) ? 'firefox' : /Chrome\/|Chromium\/|Edg\//.test(ua) ? 'chromium' : 'other'
  const standalone = env.displayModeStandalone || env.navigatorStandalone === true
  const base = { os, browser, standalone, persisted, warnings: [] as PlatformInfo['warnings'] }
  if (!env.hasIndexedDb) return { ...base, mode: 'online_only', reason: 'unsupported' }
  if (env.native) return { ...base, standalone: true, mode: 'field' }
  if (env.devOverride) return { ...base, mode: 'field' }
  if (browser === 'safari') return standalone ? { ...base, mode: 'field' } : { ...base, mode: 'online_only', reason: 'install_required' }
  return { ...base, mode: 'field', warnings: persisted === false ? ['storage_not_persistent'] : [] }
}

/** Reads the real environment and asks for persistent storage (once per launch; a no-op where already granted). */
export async function evaluatePlatform(): Promise<PlatformInfo> {
  const nav = globalThis.navigator as Navigator & { standalone?: boolean }
  const native = isNative()
  if (native) followNativeApp()               // the app's state and Android's BACK button, from the first screen on (M19b)
  let persisted: boolean | null = null
  try {
    if (!native && nav.storage?.persisted) persisted = await nav.storage.persisted()
    if (persisted === false && nav.storage?.persist) persisted = await nav.storage.persist()
  } catch { persisted = null }
  let devOverride = false
  try { devOverride = import.meta.env.DEV && localStorage.getItem('gk.dev.field') === '1' } catch { /* storage blocked */ }
  return detect({
    ua: nav.userAgent, platform: nav.platform, maxTouchPoints: nav.maxTouchPoints,
    displayModeStandalone: typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches,
    navigatorStandalone: nav.standalone, hasIndexedDb: typeof indexedDB !== 'undefined', devOverride, native,
  }, persisted)
}
